

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewCreatorOnboardingModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone: '',
        instagram_handle: '',
        instagram_url: '',
        location: '',
        influencer_category: '',
        tiktok_link: '',
        how_did_find_out: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            setFormData({
                first_name: '', middle_name: '', last_name: '', email: '', phone: '',
                instagram_handle: '', instagram_url: '', location: '', influencer_category: '',
                tiktok_link: '', how_did_find_out: ''
            });
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.first_name) errors.first_name = 'First Name is required';
        if (!formData.last_name) errors.last_name = 'Last Name is required';
        if (!formData.email) errors.email = 'Email is required';
        if (!formData.phone) errors.phone = 'Phone is required';
        if (!formData.instagram_url) errors.instagram_url = 'Instagram URL is required';
        if (!formData.location) errors.location = 'Location is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
        } catch (error) {
            console.error('Error saving Creator Onboarding:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Creator Onboarding',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Creator Onboarding</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.first_name}
                                        helperText={formErrors.first_name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Middle Name"
                                        name="middle_name"
                                        value={formData.middle_name}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.last_name}
                                        helperText={formErrors.last_name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={!!formErrors.email}
                                        helperText={formErrors.email}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        error={!!formErrors.phone}
                                        helperText={formErrors.phone}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Instagram Handle"
                                        name="instagram_handle"
                                        value={formData.instagram_handle}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Instagram URL"
                                        name="instagram_url"
                                        type="url"
                                        value={formData.instagram_url}
                                        onChange={handleInputChange}
                                        error={!!formErrors.instagram_url}
                                        helperText={formErrors.instagram_url}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.location} required>
                                        <InputLabel>Location</InputLabel>
                                        <Select
                                            name="location"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            label="Location"

                                        >
                                            <MenuItem value=""><em>Select Location</em></MenuItem>
                                            <MenuItem value="United States">United States</MenuItem>
                                            <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                                            <MenuItem value="Australia">Australia</MenuItem>
                                            <MenuItem value="Canada">Canada</MenuItem>
                                            <MenuItem value="India">India</MenuItem>
                                            {/* Add more locations as needed */}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Influencer Category</InputLabel>
                                        <Select
                                            name="influencer_category"
                                            value={formData.influencer_category}
                                            onChange={handleInputChange}
                                            label="Influencer Category"

                                        >
                                            <MenuItem value=""><em>Select Category</em></MenuItem>
                                            <MenuItem value="Travel">Travel</MenuItem>
                                            <MenuItem value="Food">Food</MenuItem>
                                            <MenuItem value="Fashion">Fashion</MenuItem>
                                            <MenuItem value="Lifestyle">Lifestyle</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="TikTok Link"
                                        name="tiktok_link"
                                        type="url"
                                        value={formData.tiktok_link}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="How did find out about Plotpackers"
                                        name="how_did_find_out"
                                        multiline
                                        rows={3}
                                        value={formData.how_did_find_out}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Creator Onboarding'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewCreatorOnboardingModal;

